/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { TicketContainer, TICKET_CHANGE_EVENTS, TicketLayout } from '../tickets/TicketContainer';
import { Texture } from 'pixi.js-legacy';
import { TicketComponentHelper } from './TicketComponentHelper';
import { PLAN_CONSTANTS, Ticket } from '@touchplan/common';
import { convertHexStringToNumber } from 'utils/color-utils';
import { TicketUtils } from '@appCanvasCommon/utils/TicketUtils';
import { MILESTONE_SIZE_OFFSET2 } from '../../canvas-common/interfaces/milestone-size.interface';
import { Side } from '@touchplan/common';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { cssVar } from '@touchplan/css-var';
import { TicketType } from '@touchplan/common';

export enum TICKET_BORDER_COLORS {
	SELECTED = '#49afcb',
	CYCLE_SELECTED = '#FFFD54',
	OUT_OF_SEQUENCE = '#C32A3C',
}

export class TicketSelected extends TicketComponentHelper implements ComponentInterface {
	public override renderable: boolean = false;

	private _activeTexture: Texture;
	private _pullTexture: Texture;

	constructor(
		texture: Texture,
		pullTexture: Texture,
		canvasAngularLand: CanvasAngularLand,
		ticketContainer: TicketContainer
	) {
		super(texture, canvasAngularLand, ticketContainer);

		this._activeTexture = texture;
		this._pullTexture = pullTexture;

		this.alpha = 1.0;

		ticketContainer.on(TICKET_CHANGE_EVENTS.CHANGED, this.onChanged);

		this.onChanged(ticketContainer);
	}

	public update(canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		const data: Ticket = ticketContainer.getData();
		const layout: TicketLayout = ticketContainer.getLayout();
		this.renderable =
			data['selected'] || data['cycleSelected'] || (data.view.hasPredecessorOutOfOrder as boolean);
		if (this.visible && this.renderable) {
			this._applyOffset(data, layout);
		}
	}

	private onChanged = (ticketContainer: TicketContainer) => {
		const data: Ticket = ticketContainer.getData();
		const layout: TicketLayout = ticketContainer.getLayout();
		this.renderable =
			data['selected'] || data['cycleSelected'] || (data.view.hasPredecessorOutOfOrder as boolean);

		if (this.renderable) {
			if (data.view.side === Side.PULL) {
				this.texture = this._pullTexture;
			} else {
				this.texture = this._activeTexture;
			}

			this._applyOffset(data, layout);

			if (data['selected']) {
				if (data['cycleSelected']) {
					this.tint = convertHexStringToNumber(TICKET_BORDER_COLORS.CYCLE_SELECTED);
					ticketContainer.cutInLine();
				} else {
					this.tint = convertHexStringToNumber(TICKET_BORDER_COLORS.SELECTED);
				}
			} else if (data['isGhost']) {
				const color =
					data.view.liveRole && data.view.liveRole.view.color ? data.view.liveRole.view.color : cssVar.black;
				this.tint = convertHexStringToNumber(color);
			} else if (data.view.hasPredecessorOutOfOrder) {
				this.tint = convertHexStringToNumber(TICKET_BORDER_COLORS.OUT_OF_SEQUENCE);
			}
		}
	};

	private _applyOffset(ticket: Ticket, layout: TicketLayout): void {
		if (TicketUtils.getTicketType(ticket) === TicketType.Milestone) {
			if (ticket.view.side === Side.PULL) {
				var sizeOffset = MILESTONE_SIZE_OFFSET2.PULL;
			} else {
				var sizeOffset = MILESTONE_SIZE_OFFSET2.ACTIVE;
			}
			this.x = -PLAN_CONSTANTS.MILESTONE_BORDER_WIDTH - sizeOffset / 2;
			this.y = -PLAN_CONSTANTS.MILESTONE_BORDER_WIDTH - sizeOffset / 2;
			this.width = layout.width + sizeOffset + PLAN_CONSTANTS.MILESTONE_BORDER_WIDTH * 2;
			this.height = layout.height + sizeOffset + PLAN_CONSTANTS.MILESTONE_BORDER_WIDTH * 2;
		} else if (TicketUtils.getTicketType(ticket) === TicketType.Constraint) {
			this.x = -PLAN_CONSTANTS.BORDER_WIDTH;
			this.y = -PLAN_CONSTANTS.BORDER_WIDTH;
			this.width = layout.width + PLAN_CONSTANTS.BORDER_WIDTH * 2;
			this.height = layout.height + PLAN_CONSTANTS.BORDER_WIDTH * 2;
		}
	}

	public override destroy(options?) {
		this.ticket.off(TICKET_CHANGE_EVENTS.CHANGED, this.onChanged);
		super.destroy(options);
	}
}
